/**
 * @module
 *
 * @import {Controller} from '@acng/frontend-stargazer'
 */
import {
  AND,
  ARRAY,
  CONST,
  ENUM,
  guard,
  INTEGER,
  IS,
  IS_ELEMENT,
  IS_TEMPLATE,
  NULLABLE,
  OBJECT,
  OR,
  STRING,
} from '@acng/frontend-rubicon';
import {get, put} from '../core/service/backend.js';
import {isHTMLElement} from '@acng/frontend-bounty/dom/type.js';
import {create, TEMPLATE} from '@acng/frontend-bounty/dom/element.js';
import {after} from '@acng/frontend-bounty/dom/move.js';
import {append} from '@acng/frontend-bounty/dom/template.js';
import {COMPILE, DESTROY, Engine, getConfig, task, UPGRADE} from '@acng/frontend-stargazer';
import {media} from 'acng/core/service/env.js';
import {closestData} from '@acng/frontend-bounty';
import {get as getAmateur} from '../amateurPool/service/cache.js';
import {assign} from '@acng/frontend-bounty/object.js';
import {popup} from '@acng/frontend-discovery';
import {t} from 'acng/locale/config/translate.js';
import {inject} from 'acng/core/service/ng.js';
import {ClickPopupController} from 'acng/layout/attribute/click-popup.js';
import {parseInteger} from '@acng/frontend-bounty/std/number.js';

const christmas = {
  day: 0,

  /**
   * Open the christmas calendar.
   */
  click: new ClickPopupController('special.christmas2025.calendar', 1e7),

  /**
   * @type {Controller}
   */
  doors: {
    [COMPILE](element, comment, engine) {
      ASSERT: guard(comment, IS(Comment));

      if (!isHTMLElement(element, TEMPLATE)) {
        const template = create(TEMPLATE);
        append(template, element.cloneNode(true));
        task(engine, new Engine(template).lookup());

        return template;
      }
    },
    async [UPGRADE](template, node, calendarEngine) {
      /**
       * @type {Engine[]}
       */
      let engines = [];
      /**
       * @type {ChildNode[]}
       */
      let nodes = [];
      const data = await get('user/xmas');
      const element = template.parentElement;

      ASSERT: {
        guard(template, IS_TEMPLATE);
        guard(element, IS_ELEMENT);
        guard(data, CALENDAR);
      }

      /**
       * @param {Valid<CALENDAR>} calendar
       */
      const create = (calendar) => {
        const doors = calendar.doors.map((item) => new ChristmasCalendarDoor(item));
        christmas.day = data.date;

        for (const door of doors) {
          const engine = new Engine(template);
          const fragment = engine.link({door}, element);
          nodes.push(...fragment.childNodes);
          after(template, fragment);
          engine.connect();
          engines.push(engine);
        }
      };

      const destroy = () => {
        nodes.forEach((node) => node.remove());
        engines.forEach((engine) => engine.disconnect());
        nodes.length = 0;
        engines.length = 0;
      };

      element.addEventListener('click', ({target}) => {
        closestData(target, 'day', async (day, element) => {
          const data = await put(`user/xmas/${day}`, {});
          ASSERT: guard(data, CLICK_RESPONSE);
          destroy();
          create(data);
          const doorData = data.doors.find((door) => door.xmas_day == parseInteger(day));
          ASSERT: guard(doorData, DOOR);
          const door = new ChristmasCalendarDoor(doorData);

          switch (data.notify) {
            case 'payment':
              inject('payment').overlay('userPool.xmas.payment');
              break;
            case 'content':
              const file = `${media.content.movies}/mediafiles/fhg/xmas2025/${door.content.nickname}.${data.content}.${door.content.type}`;
              const div = document.createElement('div');
              const contentEngine = await Engine.root.render('special', 'christmas2025', 'content');
              div.append(
                contentEngine.link(
                  {
                    file,
                    door,
                    /**
                     * @type {Controller}
                     */
                    closeContent: {
                      [UPGRADE]: (element) => {
                        element.addEventListener('click', () => popup(template).close(div));
                      },
                    },
                    /**
                     * @type {Controller}
                     */
                    clickProfile: {
                      [UPGRADE]: (element) => {
                        element.addEventListener('click', () => {
                          popup(template).close(div);
                          door.amateur.then(amateur => amateur.goto());
                        });
                        // @ts-ignore
                        return getConfig(calendarEngine).close;
                      },
                    },
                  },
                  div,
                  () => {},
                ),
              );
              popup(template).show(div, async () => {
                contentEngine.connect();
              }, async () => {
                contentEngine.disconnect();
              });
              break;
            default:
              const msg = document.createElement('div');
              msg.classList.add('notify');
              msg.addEventListener('click', () => popup(template).close(msg));

              msg.textContent = await t(`userPool.xmas.${data.notify}`, {
                date: data.date,
                coins: door?.coins,
              });

              popup(template).show(msg);
          }
        });
      });

      create(data);

      return {[DESTROY]: destroy};
    },
  },
};

class ChristmasCalendarDoor {
  /**
   * @param {Valid<DOOR>} door
   */
  constructor(door) {
    ASSERT: {
      this.xmas_day = door.xmas_day;
      this.coins = door.coins;
      this.created_at = door.created_at;
      this.cashed_at = door.cashed_at;
      this.content = door.content;
    }

    assign(this, door);
  }

  get className() {
    const day = this;
    const classes = ['day', 'day' + day.xmas_day];
    if (day.xmas_day == christmas.day) {
      classes.push('active');
    }
    if (day.created_at) {
      classes.push('created');
    }
    if (day.cashed_at) {
      classes.push('cashed');
    }
    if (day.content) {
      classes.push('content');
    }

    if (day.coins) {
      if (day.coins > 180) {
        classes.push('coins-xxl');
      } else if (day.coins > 60) {
        classes.push('coins-xl');
      } else if (day.coins > 20) {
        classes.push('coins-l');
      } else if (day.coins > 10) {
        classes.push('coins-m');
      } else {
        classes.push('coins-s');
      }
    }

    return classes.join(' ');
  }

  get style() {
    if (this.cashed_at && this.content) {
      return `background-image: url("${media.content.movies}/mediafiles/fhg/xmas2025/${this.content.nickname}.jpg")`;
    }

    return null;
  }

  get amateur() {
    return getAmateur(`${this.content.userId}`);
  }
}

const contentTypePicture = 'jpg';
const contentTypeMovie = 'mp4';

/**
 * @type {['jpg', 'mp4']}
 */
const contentTypes = [contentTypePicture, contentTypeMovie];

const CONTENT = /* @__PURE__ */ OBJECT({
  userId: INTEGER,
  nickname: STRING,
  type: /* @__PURE__ */ ENUM(contentTypes),
});

const DOOR = /* @__PURE__ */ OBJECT({
  xmas_day: INTEGER,
  coins: /* @__PURE__ */ NULLABLE(INTEGER),
  created_at: /* @__PURE__ */ NULLABLE(STRING),
  cashed_at: /* @__PURE__ */ NULLABLE(STRING),
  content: CONTENT,
});

const CALENDAR = /* @__PURE__ */ OBJECT({
  doors: /* @__PURE__ */ ARRAY(DOOR),
  date: INTEGER,
});

const CLICK_RESPONSE = /* @__PURE__ */ AND(
  CALENDAR,
  /* @__PURE__ */ OBJECT({
    notify: /* @__PURE__ */ ENUM(
      /**
       * @type {const}
       */ (['cashed', 'content', 'empty', 'created', 'payment', 'other']),
    ),
    content: OR(CONST(false), STRING),
  }),
);

export {christmas};
